import * as z from 'zod';
import type { $ZodErrorMap, $ZodIssue, $ZodRawIssue } from 'zod/v4/core';
import { catalog } from './catalog.ts';
import {
  collectTranslationKeyPaths,
  readTranslationString,
  type TranslationKeyPath,
} from './key-paths.ts';

/**
 * Any key in the `validation` namespace. Derived from the catalog, so a new
 * message is usable — and nameable from `params.i18nKey` — as soon as it is
 * added to `locales/en/validation.json`.
 */
export type ValidationKey = TranslationKeyPath<(typeof catalog)['validation']['en']>;

export type ValidationValues = Record<string, string | number>;

export type TranslateValidation = (
  key: ValidationKey,
  options?: { count?: number } & ValidationValues,
) => string;

export type ValidationIssueParams = { i18nKey: ValidationKey; values?: ValidationValues };

/**
 * Names a validation catalog key on a Zod custom issue so renames fail at compile time.
 * `values` fills `{{placeholders}}` in the message, for copy that quotes a limit owned by code.
 */
export const validationIssue = (
  key: ValidationKey,
  values?: ValidationValues,
): ValidationIssueParams => (values === undefined ? { i18nKey: key } : { i18nKey: key, values });

const englishValidation = catalog.validation.en;

// Lazy: this module is imported from `zod-init` before `z.config({ jitless: true })`
// runs, and `collectTranslationKeyPaths` parses with Zod at call time.
let validationKeyPaths: ReadonlySet<string> | undefined;

const getValidationKeyPaths = (): ReadonlySet<string> => {
  if (validationKeyPaths === undefined) {
    validationKeyPaths = new Set(collectTranslationKeyPaths(englishValidation));
  }

  return validationKeyPaths;
};

const buildI18nKeyParamsSchema = () =>
  z.object({
    i18nKey: z.string(),
    values: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  });

let i18nKeyParamsSchema: ReturnType<typeof buildI18nKeyParamsSchema> | undefined;

const getI18nKeyParamsSchema = () => {
  if (i18nKeyParamsSchema === undefined) {
    i18nKeyParamsSchema = buildI18nKeyParamsSchema();
  }

  return i18nKeyParamsSchema;
};

const isValidationKey = (value: string): value is ValidationKey =>
  getValidationKeyPaths().has(value);

/**
 * Resolves a validation key against the English catalog without an i18next
 * instance, for the global `z.config({ customError })` floor that covers parses
 * outside a request: scripts, GraphQL, and non-form validation.
 */
export const englishValidationT: TranslateValidation = (key, options) => {
  const count = options?.count;
  const template = readTranslationString(englishValidation, key, count) ?? key;
  const placeholders = options ?? {};

  let message = template;
  for (const [name, value] of Object.entries(placeholders)) {
    message = message.replaceAll(`{{${name}}}`, String(value));
  }

  return message;
};

/**
 * Translates a Zod issue through the `validation` namespace.
 *
 * Returning `undefined` is load-bearing: Zod's `finalizeIssue` chains every
 * message source with `??`, so an unhandled issue falls through to the next
 * source and ultimately to Zod's own wording. That is what lets this map stay
 * small while still covering every schema in the repo.
 */
export const createZodErrorMap = (t: TranslateValidation): $ZodErrorMap<$ZodIssue> => {
  // Zod hands the map a raw issue, which differs from the finalized `$ZodIssue`
  // in that `message` and `path` are still optional.
  return (issue: $ZodRawIssue<$ZodIssue>): string | undefined => {
    if (issue.code === 'custom') {
      const params = getI18nKeyParamsSchema().safeParse(issue.params);

      if (!params.success || !isValidationKey(params.data.i18nKey)) {
        return undefined;
      }

      return params.data.values === undefined
        ? t(params.data.i18nKey)
        : t(params.data.i18nKey, params.data.values);
    }

    // A missing form field arrives as `undefined` rather than an empty string,
    // because `zfd.text` strips empty strings before validating. A submitted
    // value that is merely the wrong type or a stale enum member must keep
    // Zod's own wording — calling it "Required" hides that the field was filled.
    if (
      (issue.code === 'invalid_type' || issue.code === 'invalid_value') &&
      issue.input === undefined
    ) {
      return t('required');
    }

    if (issue.code === 'invalid_format') {
      return issue.format === 'email' ? t('email.invalid') : undefined;
    }

    // Length copy is string-specific, so arrays, sets, and files keep Zod's own
    // wording rather than being told they need "characters".
    if (issue.code === 'too_small') {
      return issue.origin === 'string'
        ? t('text.tooShort', { count: Number(issue.minimum) })
        : undefined;
    }

    if (issue.code === 'too_big') {
      return issue.origin === 'string'
        ? t('text.tooLong', { count: Number(issue.maximum) })
        : undefined;
    }

    return undefined;
  };
};
